import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Mail, Shield, Newspaper, MessageSquare, Clock, HelpCircle, Twitter, Linkedin, ExternalLink } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const { t } = useTranslation();

  const emails = [
    {
      key: "general",
      email: "hello@chronostimepuzzle.com",
      icon: <Mail className="w-5 h-5 text-amber-300" />,
    },
    {
      key: "privacy",
      email: "privacy@chronostimepuzzle.com",
      icon: <Shield className="w-5 h-5 text-emerald-300" />,
    },
    {
      key: "press",
      email: "press@chronostimepuzzle.com",
      icon: <Newspaper className="w-5 h-5 text-teal-300" />,
      cta: { to: "/press", label: t("contact.email_press_cta") },
    },
    {
      key: "beta",
      email: "beta@chronostimepuzzle.com",
      icon: <MessageSquare className="w-5 h-5 text-fuchsia-300" />,
    },
  ];

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Mail className="w-5 h-5 text-teal-300" /> {t("contact.title")}
        </div>
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pb-20 text-stone-200" data-testid="contact-content">
        <section className="pt-4 pb-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("contact.kicker")}</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-black text-stone-50">{t("contact.title")}</h1>
          <p className="mt-6 text-lg text-stone-300 leading-relaxed max-w-2xl mx-auto italic">{t("contact.lead")}</p>
          <div className="mt-6 inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-sm text-emerald-300" data-testid="contact-response-time">
            <Clock className="w-4 h-4" /> {t("contact.response_time")}
          </div>
        </section>

        <section className="space-y-4 py-6 border-t border-white/5" data-testid="contact-emails">
          <h2 className="font-serif text-2xl font-bold text-stone-50 text-center mb-6">{t("contact.emails_h")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {emails.map((e) => (
              <div key={e.key} className="glass rounded-2xl p-5 space-y-3 flex flex-col" data-testid={`contact-email-${e.key}`}>
                <div className="flex items-center gap-2">
                  {e.icon}
                  <h3 className="font-serif text-base font-bold text-stone-50">{t(`contact.email_${e.key}_h`)}</h3>
                </div>
                <p className="text-sm text-stone-300 leading-relaxed flex-1">{t(`contact.email_${e.key}_p`)}</p>
                <a
                  href={`mailto:${e.email}`}
                  className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm font-medium underline underline-offset-4 break-all"
                  data-testid={`contact-link-${e.key}`}
                >
                  <Mail className="w-3.5 h-3.5 shrink-0" /> {e.email}
                </a>
                {e.cta && (
                  <Link to={e.cta.to} className="inline-flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200" data-testid={`contact-cta-${e.key}`}>
                    {e.cta.label} <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4 py-8 border-t border-white/5" data-testid="contact-social">
          <h2 className="font-serif text-2xl font-bold text-stone-50 text-center">{t("contact.social_h")}</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://x.com/ChronosPuzzle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 glass rounded-full px-5 py-3 text-sm text-stone-200 hover:text-amber-200 transition-colors"
              data-testid="contact-social-x"
            >
              <Twitter className="w-4 h-4" /> @ChronosPuzzle
            </a>
            <a
              href="https://www.linkedin.com/company/chronos-time-puzzle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 glass rounded-full px-5 py-3 text-sm text-stone-200 hover:text-amber-200 transition-colors"
              data-testid="contact-social-linkedin"
            >
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
            <a
              href="https://www.indiehackers.com/product/chronos-time-puzzle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 glass rounded-full px-5 py-3 text-sm text-stone-200 hover:text-amber-200 transition-colors"
              data-testid="contact-social-indiehackers"
            >
              <ExternalLink className="w-4 h-4" /> IndieHackers
            </a>
          </div>
        </section>

        <section className="mt-6 rounded-3xl glass-strong p-6 sm:p-8 text-center space-y-3" data-testid="contact-faq-shortcut">
          <HelpCircle className="w-7 h-7 text-amber-300 mx-auto" />
          <h3 className="font-serif text-xl font-bold text-stone-50">{t("contact.faq_h")}</h3>
          <p className="text-sm text-stone-300">{t("contact.faq_p")}</p>
          <Link to="/#faq-section">
            <Button className="mt-2 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6" data-testid="contact-faq-btn">
              {t("contact.faq_btn")}
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
}
